"use client"

import { router, usePage } from "@inertiajs/react"
import { Eye } from "@untitledui/icons"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import type { SharedData } from "@/types"

// Persistent "browsing as" bar, mounted once in PersistentLayout so it
// follows the impersonated session across every Inertia page. Warning
// tokens, not brand yellow — this is a caution state, not an action.
// The clock is the server's deadline, so its ending is never a surprise.
export function ImpersonationBanner() {
  const { impersonation } = usePage<SharedData>().props
  const remaining = useRemaining(impersonation?.expires_at)
  if (!impersonation) return null

  return (
    <div
      role="status"
      className="bg-warning-primary border-warning-300 sticky top-0 z-50 flex items-center justify-center gap-3 border-b px-4 py-2"
    >
      <Eye
        aria-hidden="true"
        className="text-warning-primary size-4 shrink-0"
      />
      <p className="text-warning-primary text-sm font-medium">
        Viewing as {impersonation.user_name}
        {remaining && <span className="font-normal"> · {remaining}</span>}
      </p>
      <Button
        size="sm"
        color="secondary"
        onClick={() => router.delete(impersonation.stop_path)}
      >
        Stop impersonating
      </Button>
    </div>
  )
}

/** "42 min left", refreshed each half minute; "ending now" once it has passed. */
function useRemaining(expiresAt: string | undefined) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!expiresAt) return
    const timer = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(timer)
  }, [expiresAt])
  if (!expiresAt) return null

  const minutes = Math.ceil((Date.parse(expiresAt) - now) / 60_000)
  if (minutes <= 0) return "ending now"
  if (minutes < 60) return `${minutes} min left`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours} h ${rest} min left` : `${hours} h left`
}
